import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import api from '../services/api';
import UserViewPreferences from './UserViewPreferences';
import PinModal from './PinModal';

const MobileDashboard = ({ user, onNavigate, currentView = 'dashboard', viewPreferences = {}, commandPreferences = {}, onViewPreferencesChange }) => {
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Modo oscuro
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('mobileDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Alertas - UNIFICADO con CriticalAlerts.js
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  // Modal de preferencias de visualización
  const [showPreferences, setShowPreferences] = useState(false);
  // PIN modal
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [resolvedAlerts, setResolvedAlerts] = useState(() => {
    const saved = localStorage.getItem('mobileAlertsResolved');
    return saved ? JSON.parse(saved) : [];
  });

  // Aplicar tema al documento
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('mobileDarkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Guardar resolvedAlerts en localStorage
  useEffect(() => {
    localStorage.setItem('mobileAlertsResolved', JSON.stringify(resolvedAlerts));
  }, [resolvedAlerts]);

  useEffect(() => {
    loadData();

    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const socket = io(apiUrl, { auth: { token: localStorage.getItem('token') } });

    socket.on('device-update', (data) => {
      const { deviceId, deviceIdMqtt, magnitudes, currentState, status, lastSeen } = data;

      setDevices(prevDevices => {
        const updatedDevices = prevDevices.map(device => {
          const isMatch = device._id === deviceId ||
                          device._id === String(deviceId) ||
                          device.deviceId === deviceIdMqtt;

          if (isMatch) {
            const updatedDevice = {
              ...device,
              status: status || device.status,
              lastSeen: lastSeen || device.lastSeen
            };

            if (currentState) {
              updatedDevice.currentState = { ...device.currentState, ...currentState };
            }

            if (magnitudes && Array.isArray(magnitudes)) {
              updatedDevice.magnitudes = magnitudes;
            }

            return updatedDevice;
          }
          return device;
        });

        checkAlerts(updatedDevices);
        setLastUpdate(new Date());
        return updatedDevices;
      });
    });

    const interval = setInterval(loadData, 30000);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, []);

  const loadData = async () => {
    try {
      setRefreshing(true);
      const [devicesRes, sitesRes] = await Promise.all([
        api.get('/api/devices'),
        api.get('/api/sites')
      ]);

      const devicesData = devicesRes.data || [];
      setDevices(devicesData);
      setSites(sitesRes.data || []);
      setLastUpdate(new Date());
      checkAlerts(devicesData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
    setRefreshing(false);
  };

  // UNIFICADO con CriticalAlerts.js
  const checkAlerts = (deviceList) => {
    const newAlerts = [];

    console.log(`🚨 MobileDashboard: ========== CHECKING ALERTS ==========`);
    console.log(`🚨 MobileDashboard: Checking ${deviceList?.length || 0} devices`);

    const getAllowedDevices = () => {
      if (!Array.isArray(deviceList)) return [];
      if (!user || user.role === 'admin') return deviceList;
      if (!user.devicePermissions) return [];

      return deviceList.filter(device => {
        const deviceId = device._id;
        const devicePermissions = user.devicePermissions[deviceId];
        return devicePermissions && devicePermissions.allowed;
      });
    };

    const getAllowedMagnitudes = (device) => {
      if (!device.magnitudes) return [];
      if (!user || user.role === 'admin') return device.magnitudes;
      if (!user.devicePermissions || !user.devicePermissions[device._id]) return [];

      const devicePermissions = user.devicePermissions[device._id];
      if (!devicePermissions.magnitudes) return [];

      return device.magnitudes.filter(magnitude =>
        magnitude.showInDashboard !== false &&
        devicePermissions.magnitudes.includes(magnitude.name)
      );
    };

    const allowedDevices = getAllowedDevices();
    console.log(`🎯 MobileDashboard: ${allowedDevices.length} accessible devices`);

    allowedDevices.forEach(device => {
      if (device.status === 'offline') {
        console.log(`⏭️ MobileDashboard: Skipping ${device.name} - OFFLINE`);
        return;
      }

      const allowedMagnitudes = getAllowedMagnitudes(device);
      if (allowedMagnitudes.length === 0) {
        console.log(`⏭️ MobileDashboard: Skipping ${device.name} - No allowed magnitudes`);
        return;
      }

      console.log(`🔍 MobileDashboard: Checking device ${device.name} with ${allowedMagnitudes.length} magnitudes`);

      allowedMagnitudes.forEach(magnitude => {
        const currentValue = magnitude.currentValue;
        if (currentValue === undefined || currentValue === null) return;

        // Manejar magnitudes booleanas
        if (magnitude.valueType === 'boolean') {
          // Para booleanos: alertar si está en false/OFF (asumiendo que true/ON es el estado normal)
          const boolValue = currentValue === true || currentValue === 'true' || currentValue === 1 || currentValue === '1';

          console.log(`📊 MobileDashboard: ${device.name}.${magnitude.label} - BoolValue: ${boolValue} (raw: ${currentValue}), Alert: ${!boolValue}`);

          if (!boolValue) {
            newAlerts.push({
              id: `bool_${device._id}_${magnitude.name}`,
              type: 'boolean',
              device: device.name,
              deviceId: device._id,
              magnitude: magnitude.label,
              magnitudeName: magnitude.name,
              value: 'OFF',
              unit: '',
              min: null,
              max: null,
              severity: 'high'
            });
            console.log(`🚨 MobileDashboard: ALERT ADDED (boolean) - ${device.name}.${magnitude.label}: OFF`);
          }
          return;
        }

        // Manejar magnitudes numéricas
        const hasMin = magnitude.minNormalValue !== undefined && magnitude.minNormalValue !== null;
        const hasMax = magnitude.maxNormalValue !== undefined && magnitude.maxNormalValue !== null;

        if (!hasMin && !hasMax) {
          console.log(`  ⏭️ ${device.name}.${magnitude.label} - No ranges defined`);
          return;
        }

        const numValue = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;
        if (isNaN(numValue)) return;

        const isBelowMin = hasMin && numValue < magnitude.minNormalValue;
        const isAboveMax = hasMax && numValue > magnitude.maxNormalValue;
        const isOutOfRange = isBelowMin || isAboveMax;

        console.log(`📊 MobileDashboard: ${device.name}.${magnitude.label} - Value: ${numValue}, Min: ${magnitude.minNormalValue}, Max: ${magnitude.maxNormalValue}, OutOfRange: ${isOutOfRange}`);

        if (isOutOfRange) {
          newAlerts.push({
            id: `range_${device._id}_${magnitude.name}`,
            type: 'range',
            device: device.name,
            deviceId: device._id,
            magnitude: magnitude.label,
            magnitudeName: magnitude.name,
            value: numValue,
            unit: magnitude.unit,
            min: magnitude.minNormalValue,
            max: magnitude.maxNormalValue,
            severity: 'high'
          });
          console.log(`🚨 MobileDashboard: ALERT ADDED - ${device.name}.${magnitude.label}: ${numValue}`);
        }
      });
    });

    console.log(`🚨 MobileDashboard: FINAL RESULT - Found ${newAlerts.length} alerts`);

    setAlerts(prevAlerts => {
      const newAlertIds = new Set(newAlerts.map(a => a.id));
      const resolvedNow = prevAlerts.filter(a => !newAlertIds.has(a.id));

      if (resolvedNow.length > 0) {
        setResolvedAlerts(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const newResolved = resolvedNow
            .filter(a => !existingIds.has(a.id))
            .map(a => ({ ...a, resolvedAt: new Date(), isResolved: true }));
          return [...prev, ...newResolved];
        });
      }

      return newAlerts;
    });
  };

  const getVisibleDevices = () => {
    if (!Array.isArray(devices)) return [];
    if (!user || user.role === 'admin') return devices;
    if (!user.devicePermissions) return [];

    return devices.filter(device => {
      const deviceId = device._id;
      const devicePermissions = user.devicePermissions[deviceId];
      return devicePermissions && devicePermissions.allowed;
    });
  };

  const filterMagnitudesByPermissions = (device) => {
    if (!device.magnitudes) return [];
    // Filtrar primero por showInDashboard
    let visibleMagnitudes = device.magnitudes.filter(mag => mag.showInDashboard !== false);

    // Filtrar por permisos de dispositivo (admin ve todo)
    if (user && user.role !== 'admin') {
      if (!user.devicePermissions || !user.devicePermissions[device._id]) return [];
      const devicePermissions = user.devicePermissions[device._id];
      if (!devicePermissions.magnitudes) return [];
      visibleMagnitudes = visibleMagnitudes.filter(magnitude =>
        devicePermissions.magnitudes.includes(magnitude.name)
      );
    }

    // Aplicar preferencias de visualización del usuario
    const userPrefs = viewPreferences[device._id];
    if (userPrefs !== undefined && Array.isArray(userPrefs)) {
      if (userPrefs.length === 0) {
        return []; // Usuario ha deshabilitado todas las magnitudes
      } else {
        visibleMagnitudes = visibleMagnitudes.filter(magnitude =>
          userPrefs.includes(magnitude.name)
        );
      }
    }

    return visibleMagnitudes;
  };

  // Manejar click en boton de preferencias (verificar PIN si existe)
  const handlePreferencesClick = async () => {
    try {
      const response = await api.get('/api/users/me/has-pin');
      if (response.data.hasPin && !pinVerified) {
        setShowPinModal(true);
      } else {
        setShowPreferences(true);
      }
    } catch (error) {
      console.error('Error checking PIN:', error);
      // Si hay error, intentar abrir directamente
      setShowPreferences(true);
    }
  };

  const handlePinSuccess = () => {
    setShowPinModal(false);
    setPinVerified(true);
    setShowPreferences(true);
  };

  const getDeviceStatus = (device) => {
    if (device.status === 'offline') return 'offline';
    if (!device.lastSeen) return 'offline';
    const lastSeen = new Date(device.lastSeen);
    const now = new Date();
    if (lastSeen > now) return 'online';
    const minutesAgo = (now - lastSeen) / 60000;
    const thresholdMin = device.site?.offlineThresholdMinutes || 60;
    const warningMin = thresholdMin === 15 ? 5 : 15;
    if (minutesAgo >= thresholdMin) return 'offline';
    if (minutesAgo >= warningMin) return 'warning';
    return 'online';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'offline': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'En linea';
      case 'warning': return 'Intermitente';
      case 'offline': return 'Desconectado';
      default: return 'Desconocido';
    }
  };

  const formatValue = (magnitude) => {
    const value = magnitude.currentValue;
    if (value === undefined || value === null) return '---';

    if (magnitude.valueType === 'boolean') {
      return (value === true || value === 'true' || value === '1' || value === 1) ? 'ON' : 'OFF';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);

    const decimals = magnitude.decimals || 1;
    return numValue.toFixed(decimals);
  };

  const getMagnitudeStatus = (magnitude) => {
    const value = magnitude.currentValue;
    if (value === undefined || value === null) return 'unknown';

    // Para booleanos: OFF es abnormal, ON es normal
    if (magnitude.valueType === 'boolean') {
      const boolValue = value === true || value === 'true' || value === 1 || value === '1';
      return boolValue ? 'normal' : 'abnormal';
    }

    // UNIFICADO con checkAlerts - verificar si AL MENOS UNO de los rangos está definido
    const hasMin = magnitude.minNormalValue !== undefined && magnitude.minNormalValue !== null;
    const hasMax = magnitude.maxNormalValue !== undefined && magnitude.maxNormalValue !== null;

    if (!hasMin && !hasMax) return 'normal'; // Sin rangos definidos

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'normal';

    // Verificar si está fuera de rango (mismo criterio que checkAlerts)
    const isBelowMin = hasMin && numValue < magnitude.minNormalValue;
    const isAboveMax = hasMax && numValue > magnitude.maxNormalValue;

    if (isBelowMin || isAboveMax) {
      return 'abnormal';
    }

    return 'normal';
  };

  const filteredDevices = getVisibleDevices();

  if (loading) {
    return (
      <div className="mobile-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
            <span className="loading-spinner" style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid var(--border-color)',
              borderTop: '3px solid var(--primary-color)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>Cargando dashboard...</div>
        </div>
      </div>
    );
  }

  const activeAlertCount = alerts.length;
  const resolvedAlertCount = resolvedAlerts.length;
  const allAlerts = [...alerts, ...resolvedAlerts.map(a => ({ ...a, isResolved: true }))];

  return (
    <div className="mobile-container">
      {/* Header */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              padding: '0.5rem',
              border: '1px solid var(--border-color)',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src="/logo-designerlow.png"
                alt="Logo"
                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
              />
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                Dashboard IoT
              </h1>
              <p style={{
                margin: '0.125rem 0 0 0',
                color: 'var(--text-secondary)',
                fontSize: '0.8rem'
              }}>
                Hola, {user?.username}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              className="theme-toggle"
              onClick={handlePreferencesClick}
              title="Preferencias de visualización"
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '0.5rem',
                cursor: 'pointer',
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              👁️
            </button>
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        <div style={{
          marginTop: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--text-secondary)'
        }}>
          <span>🕒</span>
          <span>Actualizado: {lastUpdate.toLocaleTimeString('es-AR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}</span>
        </div>
      </div>

      {/* Alertas Compactas */}
      {(activeAlertCount > 0 || resolvedAlertCount > 0) && (
        <div className="mobile-alerts-container" style={{
          borderColor: activeAlertCount > 0 ? 'var(--danger-color)' : 'var(--success-color)'
        }}>
          <div
            onClick={() => setShowAlerts(!showAlerts)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className={activeAlertCount > 0 ? 'alert-pulse' : ''} style={{
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: activeAlertCount > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'
              }}>
                {activeAlertCount > 0 ? '🚨' : '✅'}
              </span>
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                  {activeAlertCount > 0
                    ? `${activeAlertCount} Alerta${activeAlertCount !== 1 ? 's' : ''} activa${activeAlertCount !== 1 ? 's' : ''}`
                    : 'Todo en orden'
                  }
                </div>
                {resolvedAlertCount > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--success-color)' }}>
                    {resolvedAlertCount} resuelta{resolvedAlertCount !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {showAlerts ? '▼' : '▶'}
            </span>
          </div>

          {showAlerts && (
            <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
              {allAlerts.map(alert => (
                <div
                  key={alert.id}
                  className={`mobile-alert-item ${alert.isResolved ? 'resolved' : 'critical'}`}
                >
                  <span style={{ fontSize: '1.25rem' }}>
                    {alert.isResolved ? '✅' : '🔴'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                      {alert.device} - {alert.magnitude}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {alert.isResolved
                        ? 'Volvió al estado normal'
                        : alert.type === 'boolean'
                          ? `Estado: ${alert.value} (normal: ON)`
                          : `${alert.value}${alert.unit || ''} (normal: ${alert.min}-${alert.max})`
                      }
                    </div>
                  </div>
                  {alert.isResolved && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setResolvedAlerts(prev => prev.filter(a => a.id !== alert.id));
                      }}
                      style={{
                        background: 'var(--success-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      OK
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grid de Dispositivos */}
      <div className="mobile-devices-grid">
        {filteredDevices.map(device => {
          const status = getDeviceStatus(device);
          const statusColor = getStatusColor(status);
          const visibleMagnitudes = filterMagnitudesByPermissions(device);
          const hasAbnormal = visibleMagnitudes.some(m => getMagnitudeStatus(m) === 'abnormal');

          // Colores según estado del dispositivo
          const isOnline = status === 'online';
          const isWarning = status === 'warning';
          const deviceBorderColor = isWarning ? 'rgba(245, 158, 11, 0.6)' : (isOnline ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)');
          const deviceBgColor = isWarning ? 'rgba(245, 158, 11, 0.08)' : (isOnline ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)');
          const deviceShadowColor = isWarning ? 'rgba(245, 158, 11, 0.15)' : (isOnline ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)');

          return (
            <div
              key={device._id}
              className={`mobile-device-card ${hasAbnormal ? 'has-alert' : ''}`}
              style={{
                border: `2px solid ${deviceBorderColor}`,
                background: deviceBgColor,
                boxShadow: `0 0 8px ${deviceShadowColor}`
              }}
            >
              {/* Header del dispositivo */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>📱</span>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: hasAbnormal ? 'var(--danger-color)' : 'var(--text-primary)'
                  }}>
                    {device.name}
                  </h3>
                  {hasAbnormal && (
                    <span style={{
                      background: 'var(--danger-color)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem'
                    }}>!</span>
                  )}
                </div>
                <span style={{
                  background: statusColor,
                  color: 'white',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}>
                  {getStatusText(status)}
                </span>
              </div>

              {/* Magnitudes */}
              {visibleMagnitudes.length > 0 && (
                <div className="mobile-magnitudes-grid">
                  {visibleMagnitudes.map(magnitude => {
                    const magStatus = getMagnitudeStatus(magnitude);
                    const isAbnormal = magStatus === 'abnormal';

                    return (
                      <div
                        key={magnitude.name}
                        className={`mobile-magnitude-card ${magStatus}`}
                      >
                        <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                          {magnitude.icon || '📊'}
                        </div>
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: isAbnormal ? 'var(--danger-color)' : 'var(--success-color)'
                        }}>
                          {formatValue(magnitude)}
                          <span style={{ fontSize: '0.7rem', fontWeight: '400', marginLeft: '2px' }}>
                            {magnitude.unit}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.125rem'
                        }}>
                          {magnitude.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ultima actualizacion */}
              {device.lastSeen && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.4rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  fontSize: '0.7rem',
                  color: 'var(--text-secondary)',
                  textAlign: 'center'
                }}>
                  Actualizado: {new Date(device.lastSeen).toLocaleString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredDevices.length === 0 && (
        <div className="mobile-device-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
            No hay dispositivos
          </h3>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            No tienes dispositivos asignados
          </p>
        </div>
      )}

      {/* Boton flotante de actualizar */}
      <button
        className={`mobile-fab ${refreshing ? 'refreshing' : ''}`}
        onClick={loadData}
        disabled={refreshing}
      >
        🔄
      </button>

      {/* Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <button
          className={`mobile-nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
          onClick={() => window.location.href = '/mobile'}
        >
          <span className="nav-icon">📊</span>
          <span>Dashboard</span>
        </button>
        <button
          className={`mobile-nav-item ${currentView === 'commands' ? 'active' : ''}`}
          onClick={() => window.location.href = '/mobile-commands'}
        >
          <span className="nav-icon">⚡</span>
          <span>Comandos</span>
        </button>
        <button
          className={`mobile-nav-item ${currentView === 'alerts' ? 'active' : ''}`}
          onClick={() => setShowAlerts(!showAlerts)}
          style={{ position: 'relative' }}
        >
          <span className="nav-icon">
            {activeAlertCount > 0 ? '🔔' : '🔕'}
          </span>
          <span>Alertas</span>
          {activeAlertCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '2px',
              right: '10px',
              background: 'var(--danger-color)',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '0.65rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700'
            }}>
              {activeAlertCount}
            </span>
          )}
        </button>
        <button
          className="mobile-nav-item"
          onClick={() => window.location.href = '/mobile-logout'}
        >
          <span className="nav-icon">🚪</span>
          <span>Salir</span>
        </button>
      </nav>

      {/* Modal de PIN */}
      {showPinModal && (
        <PinModal
          isOpen={showPinModal}
          onClose={() => setShowPinModal(false)}
          onSuccess={handlePinSuccess}
          mode="verify"
        />
      )}

      {/* Modal de Preferencias de Visualización */}
      {showPreferences && (
        <UserViewPreferences
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          onSave={onViewPreferencesChange}
        />
      )}
    </div>
  );
};

export default MobileDashboard;
